import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { api } from "../lib/api";
import { readableTextOn } from "../lib/colors";
import type { Employee } from "../lib/types";

// Single-line "On-Call Today" summary that lives above the schedule
// grid. Shows the primary (and secondary if any) for every roster
// for today's date. Quick at-a-glance "who's on right now."
//
// Renders nothing when the agency has no rosters set up yet.
export function OnCallTodayStrip() {
  return <OnCallTodayStripInner />;
}

function OnCallTodayStripInner() {
  const rostersQ = useQuery({
    queryKey: ["onCallRosters"],
    queryFn: () => api.listOnCallRosters(false),
  });
  const employeesQ = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.listEmployees(),
  });

  const todayISO = format(new Date(), "yyyy-MM-dd");
  const assignsQ = useQuery({
    queryKey: ["onCallAssignments", "today", todayISO],
    queryFn: () => api.listOnCallAssignments(null, todayISO, todayISO),
  });

  const rosters = useMemo(
    () => (rostersQ.data ?? []).sort((a, b) => a.sortOrder - b.sortOrder),
    [rostersQ.data],
  );
  const empById = useMemo(() => {
    const m = new Map<string, Employee>();
    for (const e of employeesQ.data ?? []) m.set(e.id, e);
    return m;
  }, [employeesQ.data]);

  if (rosters.length === 0) return null;

  function rosterAssignmentsForToday(rosterId: string) {
    const rows = (assignsQ.data ?? []).filter((a) => a.rosterId === rosterId);
    rows.sort((x, y) => x.role.localeCompare(y.role));
    return rows;
  }

  return (
    <div className="shrink-0 border-y border-slate-200 bg-white flex items-center gap-2 px-3 py-1 text-xs print:hidden">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 shrink-0">
        On-Call Today
      </span>
      <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
        {rosters.map((r) => {
          const rows = rosterAssignmentsForToday(r.id);
          if (rows.length === 0) {
            return (
              <span key={r.id} className="text-slate-400 italic">
                <span
                  className="inline-block w-2 h-2 rounded-full mr-1 align-middle opacity-40"
                  style={{ backgroundColor: r.color }}
                />
                {r.name}: —
              </span>
            );
          }
          return (
            <span key={r.id} className="flex items-center gap-1">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: r.color }}
              />
              <span className="font-medium text-slate-700">{r.name}:</span>
              {rows.map((a) => {
                const e = empById.get(a.employeeId);
                return (
                  <span
                    key={a.id}
                    className="themed-fill px-1.5 py-0.5 rounded text-[11px] font-semibold"
                    style={{
                      backgroundColor: r.color,
                      color: readableTextOn(r.color),
                      opacity: a.role === "secondary" ? 0.72 : 1,
                      ["--themed-color" as string]: r.color,
                    }}
                  >
                    {a.role === "secondary" && <span className="text-[9px] opacity-80 mr-0.5">2°</span>}
                    {e ? e.lastName : "?"}
                  </span>
                );
              })}
            </span>
          );
        })}
      </div>
    </div>
  );
}
